import { useTranslation } from "react-i18next";
import { ArrowUpDown, Clock, DollarSign, Users } from "lucide-react";
import { cn } from "@/lib/utils";

export type SortOption = "departure" | "price" | "seats";

interface SortToggleProps {
  currentSort: SortOption;
  onSortChange: (sort: SortOption) => void;
  className?: string;
}

interface SortButtonConfig {
  value: SortOption;
  labelKey: string;
  icon: React.ReactNode;
}

const sortOptions: SortButtonConfig[] = [
  {
    value: "departure",
    labelKey: "results.sortDeparture",
    icon: <Clock className="h-3.5 w-3.5" />,
  },
  {
    value: "price",
    labelKey: "results.sortPrice",
    icon: <DollarSign className="h-3.5 w-3.5" />,
  },
  {
    value: "seats",
    labelKey: "results.sortSeats",
    icon: <Users className="h-3.5 w-3.5" />,
  },
];

/**
 * SortToggle - tab-style sort selector for trip results
 *
 * Displays three options: Departure, Price, Seats
 * Active option shows with primary color and arrow indicator
 */
export function SortToggle({
  currentSort,
  onSortChange,
  className,
}: SortToggleProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-lg bg-muted p-1",
        className
      )}
      role="group"
      aria-label={t("results.sortBy")}
    >
      {sortOptions.map((option) => {
        const isActive = currentSort === option.value;
        return (
          <button
            key={option.value}
            onClick={() => onSortChange(option.value)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              isActive
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-background/50"
            )}
            aria-pressed={isActive}
            aria-label={`${t("results.sortBy")} ${t(option.labelKey)}`}
          >
            {option.icon}
            <span>{t(option.labelKey)}</span>
            {isActive && (
              <ArrowUpDown className="h-3 w-3 text-primary" aria-hidden="true" />
            )}
          </button>
        );
      })}
    </div>
  );
}
